/**
 * weatherCogPool.ts — round-robin pool of weatherCogWorker instances with
 * concurrency throttling.
 *
 * Each worker handles one job at a time (queued internally). When the caller
 * enqueues more jobs than `poolSize`, the excess waits until a worker frees up.
 * This prevents flooding the origin server with dozens of concurrent HTTP/2
 * streams — which was causing ERR_HTTP2_PROTOCOL_ERROR on bursty preloads.
 *
 * Jobs are tagged with a `generation` so that switching layers cleanly drops
 * any queued-but-not-yet-dispatched jobs and rejects any in-flight ones.
 */

export interface DecodedFrame {
  rawData: Uint8Array;
  /** Number of channels in rawData. 1 = single-band (rawData.length == w*h).
   * 4 = RGBA interleaved (rawData.length == w*h*4). The renderer uploads
   * single-channel frames as gl.R8 textures and multi-channel as gl.RGBA8. */
  channels: 1 | 4;
  width: number;
  height: number;
  bbox: number[];
  noData: number | null;
}

interface Job {
  jobId: number;
  generation: number;
  cogKey: string;
  cogApiUrl: string;
  downsample: number;
  /** Optional viewport bbox in EPSG:3857 meters. When set, the worker does
   * a windowed read of only the source pixels intersecting this bbox —
   * massive speedup at typical zooms vs reading the full raster. */
  viewportBboxMerc: number[] | null;
  /** Higher = dispatched sooner. Priority jobs are inserted ahead of
   * normal-priority jobs already waiting in the queue, so the frame the
   * user is currently looking at doesn't sit behind a full preload batch. */
  priority: number;
  /** Short-lived access token appended to the COG request as ?t=. The pool
   * is agnostic about its contents; it just forwards it to the worker. */
  token?: string;
  resolve: (v: DecodedFrame) => void;
  reject: (err: Error) => void;
  /** Set to true the first time the job settles (resolve OR reject) so no
   * later path (timeout firing after a late response, or vice-versa) can
   * settle it twice or double-free its slot. */
  settled?: boolean;
}

interface WorkerSlot {
  worker: Worker;
  busy: boolean;
  currentJobId: number | null;
  /** Watchdog timer for the in-flight job. A worker that hangs or crashes
   * without posting back done/error would otherwise pin this slot busy
   * forever, and once every slot is pinned the whole pool stops dispatching
   * (a hard preload freeze). The timer rejects the job and frees the slot. */
  timer: ReturnType<typeof setTimeout> | null;
}

export class WeatherCogPool {
  private slots: WorkerSlot[] = [];
  private queue: Job[] = [];
  private nextJobId = 1;
  // Maps in-flight jobIds → job (for matching responses to callers).
  private inFlight = new Map<number, Job>();
  private generation = 0;
  // Kept so a crashed worker (onerror) can be torn down and replaced — a dead
  // worker silently swallows future postMessages, so we must respawn it rather
  // than leave a permanently useless slot.
  private workerFactory: () => Worker;
  // Per-job watchdog timeout. A decode that neither resolves nor rejects within
  // this window (hung fetch, crashed worker that didn't post back, a frame the
  // decoder chokes on) is force-failed so its slot frees and the queue drains.
  // Generous enough not to false-fail a slow-but-valid decode on a weak device
  // or slow network; short enough that one bad frame can't wedge preload for
  // long. Callers treat a reject as "frame unavailable" and move on.
  private readonly jobTimeoutMs: number;

  constructor(workerFactory: () => Worker, size?: number, jobTimeoutMs = 30000) {
    this.workerFactory = workerFactory;
    this.jobTimeoutMs = jobTimeoutMs;
    const hw = (navigator as any).hardwareConcurrency ?? 4;
    // Cap at 3 to stay well under HTTP/2 per-connection limits and avoid
    // triggering Cloudflare's burst protection.
    const poolSize = size ?? Math.min(Math.max(1, Math.floor(hw / 2)), 3);

    for (let i = 0; i < poolSize; i++) {
      this.slots.push(this.spawnSlot());
    }
    console.log(`[WeatherCogPool] spawned ${poolSize} workers (max concurrent fetches)`);
  }

  /** Create a fresh worker slot with message + error handlers wired. Used at
   * construction and to replace a worker that crashed. */
  private spawnSlot(): WorkerSlot {
    const w = this.workerFactory();
    const slot: WorkerSlot = { worker: w, busy: false, currentJobId: null, timer: null };
    w.onmessage = (e) => this.handleMessage(e);
    // A worker-level error is an uncaught crash — distinct from a decode error,
    // which the worker catches and reports as a normal { type: "error" }
    // message. onerror bypasses that path, so WITHOUT handling here the slot's
    // in-flight job would never settle and the slot would stay busy forever.
    // We fail the job, tear the (likely-dead) worker down, and respawn so the
    // pool keeps its full concurrency.
    w.onerror = (e) => {
      console.error("[WeatherCogPool] worker crashed:", (e as any)?.message ?? e);
      this.recycleSlot(slot, new Error("worker crashed"));
    };
    return slot;
  }

  /** Fail whatever job a slot was running (if any), clear its watchdog, and
   * replace its worker with a fresh one. The old worker is terminated because
   * after an onerror it can't be trusted to process further messages. */
  private recycleSlot(slot: WorkerSlot, reason: Error) {
    if (slot.timer !== null) { clearTimeout(slot.timer); slot.timer = null; }
    const jobId = slot.currentJobId;
    if (jobId !== null) {
      const job = this.inFlight.get(jobId);
      if (job) {
        this.inFlight.delete(jobId);
        this.settleReject(job, reason);
      }
    }
    try { slot.worker.terminate(); } catch { /* already dead */ }
    // Replace the worker in place so `this.slots` keeps its length and pump()
    // sees a usable slot again.
    const idx = this.slots.indexOf(slot);
    const fresh = this.spawnSlot();
    if (idx >= 0) this.slots[idx] = fresh;
    else this.slots.push(fresh);
    this.pump();
  }

  /** Reject a job exactly once. Guards against a timeout and a late worker
   * response both trying to settle the same promise. */
  private settleReject(job: Job, err: Error) {
    if (job.settled) return;
    job.settled = true;
    job.reject(err);
  }

  /** Resolve a job exactly once (see settleReject). */
  private settleResolve(job: Job, value: DecodedFrame) {
    if (job.settled) return;
    job.settled = true;
    job.resolve(value);
  }

  /** Bump generation — queued jobs from older generations are dropped,
   * in-flight jobs are rejected so callers can clean up. */
  bumpGeneration(): number {
    this.generation++;

    // Drop queued jobs from older generations
    this.queue = this.queue.filter((job) => {
      if (job.generation < this.generation) {
        this.settleReject(job, new Error("cancelled"));
        return false;
      }
      return true;
    });

    // Reject in-flight jobs from older generations (the workers will still
    // finish their fetch, but we discard the response in handleMessage)
    for (const [id, job] of this.inFlight) {
      if (job.generation < this.generation) {
        this.settleReject(job, new Error("cancelled"));
        this.inFlight.delete(id);
      }
    }
    return this.generation;
  }

  decode(
    cogKey: string,
    cogApiUrl: string,
    downsample = 1,
    viewportBboxMerc: number[] | null = null,
    priority = 0,
    token?: string,
  ): Promise<DecodedFrame> {
    return new Promise<DecodedFrame>((resolve, reject) => {
      const job: Job = {
        jobId: this.nextJobId++,
        generation: this.generation,
        cogKey,
        cogApiUrl,
        downsample,
        viewportBboxMerc,
        priority,
        token,
        // Wrap in the once-only guards so the watchdog timeout and a late
        // worker response can't both settle this promise.
        resolve,
        reject,
      };
      // Insert keeping the queue sorted by descending priority. Within the
      // same priority, preserve insertion order (stable: only jump ahead of
      // strictly-lower-priority jobs). This lets a priority-1 "visible frame"
      // request skip a backlog of priority-0 preload jobs.
      if (priority > 0) {
        let i = this.queue.length;
        while (i > 0 && this.queue[i - 1].priority < priority) i--;
        this.queue.splice(i, 0, job);
      } else {
        this.queue.push(job);
      }
      this.pump();
    });
  }

  /** Dispatch queued jobs to free worker slots. */
  private pump() {
    for (const slot of this.slots) {
      if (slot.busy) continue;
      if (this.queue.length === 0) break;
      const job = this.queue.shift()!;
      slot.busy = true;
      slot.currentJobId = job.jobId;
      this.inFlight.set(job.jobId, job);
      // Arm the watchdog: if this job hasn't settled within jobTimeoutMs, the
      // worker is hung or dead. Fail the job and recycle the slot (which
      // terminates + respawns the worker and pumps the queue) so one bad frame
      // can't pin a slot — or, cumulatively, the whole pool — forever.
      slot.timer = setTimeout(() => {
        console.warn(`[WeatherCogPool] job ${job.jobId} (${job.cogKey}) timed out after ${this.jobTimeoutMs}ms; recycling worker`);
        this.recycleSlot(slot, new Error("decode timeout"));
      }, this.jobTimeoutMs);
      slot.worker.postMessage({
        type: "decode",
        jobId: job.jobId,
        cogKey: job.cogKey,
        cogApiUrl: job.cogApiUrl,
        downsample: job.downsample,
        viewportBboxMerc: job.viewportBboxMerc,
        token: job.token,
      });
    }
  }

  private handleMessage(e: MessageEvent) {
    const { type, jobId } = e.data ?? {};
    const job = this.inFlight.get(jobId);

    // Free the slot regardless of what happened so pump() can continue. Also
    // disarm the watchdog — the worker responded, so the timeout must not fire
    // later and recycle a healthy worker mid-next-job.
    const slot = this.slots.find((s) => s.currentJobId === jobId);
    if (slot) {
      if (slot.timer !== null) { clearTimeout(slot.timer); slot.timer = null; }
      slot.busy = false;
      slot.currentJobId = null;
    }

    if (job) {
      this.inFlight.delete(jobId);

      // Drop stale-generation results
      if (job.generation === this.generation) {
        if (type === "done") {
          this.settleResolve(job, {
            rawData: e.data.rawData,
            channels: e.data.channels ?? 4, // default RGBA for back-compat
            width: e.data.width,
            height: e.data.height,
            bbox: e.data.bbox,
            noData: e.data.noData,
          });
        } else if (type === "error") {
          this.settleReject(job, new Error(e.data.message ?? "decode failed"));
        }
      } else {
        // Stale generation: settle so the promise never dangles (it was likely
        // already rejected in bumpGeneration, and the settle guard makes this a
        // no-op in that case).
        this.settleReject(job, new Error("cancelled"));
      }
    }

    // Dispatch next queued job (if any)
    this.pump();
  }

  destroy() {
    for (const slot of this.slots) {
      if (slot.timer !== null) { clearTimeout(slot.timer); slot.timer = null; }
      slot.worker.terminate();
    }
    this.slots = [];
    this.queue.forEach((job) => this.settleReject(job, new Error("pool destroyed")));
    this.queue = [];
    this.inFlight.forEach((job) => this.settleReject(job, new Error("pool destroyed")));
    this.inFlight.clear();
  }
}